// Hook to fetch and apply tenant branding for the pre-auth login page.
// This is distinct from use-branding.ts (which requires authentication).
// Called before auth is established — uses a direct fetch rather than ApiClient.

import { useEffect, useState } from 'react'
import { SUPPORTED_FONTS } from '@/lib/api/branding'

// Re-use the same colour key → CSS variable mapping from use-branding.ts.
const COLOR_TO_CSS: Record<string, string> = {
  headerBackground: '--header-background',
  headerForeground: '--header-foreground',
  sidebarBackground: '--sidebar-background',
  sidebarForeground: '--sidebar-foreground',
  footerBackground: '--footer-background',
  footerForeground: '--footer-foreground',
  background: '--background',
  foreground: '--foreground',
  bodyForeground: '--body-foreground',
  border: '--border',
  card: '--card',
  cardForeground: '--card-foreground',
  cardHeaderForeground: '--card-header-foreground',
  buttonBackground: '--primary',
  buttonForeground: '--primary-foreground',
  gridAppetiteIn: '--grid-appetite-in',
  gridAppetiteOut: '--grid-appetite-out',
  gridNodeInherent: '--grid-node-inherent',
  gridNodeResidual: '--grid-node-residual',
  gridNodeTarget: '--grid-node-target',
}

const FONT_FAMILIES: Record<string, string> = Object.fromEntries(
  SUPPORTED_FONTS.map((f) => [f.id, f.family]),
)

interface PublicBrandingData {
  colors: Record<string, string>
  font_id: string
  logo_url?: string
}

function applyPublicBranding(data: PublicBrandingData): void {
  const root = document.documentElement

  for (const [key, cssVar] of Object.entries(COLOR_TO_CSS)) {
    const value = data.colors[key]
    if (value) {
      root.style.setProperty(cssVar, value)
    } else {
      root.style.removeProperty(cssVar)
    }
  }

  const fontFamily = FONT_FAMILIES[data.font_id] ?? FONT_FAMILIES['inter'] ?? 'Inter, sans-serif'
  root.style.setProperty('--font-family', fontFamily)
}

function clearPublicBranding(): void {
  const root = document.documentElement
  for (const cssVar of Object.values(COLOR_TO_CSS)) {
    root.style.removeProperty(cssVar)
  }
  root.style.removeProperty('--font-family')
}

/**
 * Fetches and applies tenant branding for the pre-auth login page.
 * Uses the unauthenticated /t/{slug}/branding-public endpoint.
 * Returns logo URL if configured, null otherwise.
 *
 * Branding is applied as CSS variables on the document root,
 * and cleared when the component unmounts.
 */
export function usePublicBranding(slug: string): { logoUrl: string | null } {
  const [logoUrl, setLogoUrl] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function fetchBranding(): Promise<void> {
      try {
        const response = await fetch(`/t/${encodeURIComponent(slug)}/branding-public`)
        if (!response.ok || cancelled) return

        const data = (await response.json()) as PublicBrandingData
        if (cancelled) return

        applyPublicBranding(data)
        if (data.logo_url) {
          setLogoUrl(data.logo_url)
        }
      } catch {
        // Branding is best-effort — platform defaults apply if fetch fails.
      }
    }

    fetchBranding()

    return () => {
      cancelled = true
      clearPublicBranding()
    }
  }, [slug])

  return { logoUrl }
}
